import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import Select from "react-select";
import dayjs from "dayjs";
import { MdOutlineOpenInNew, MdOutlineSearch, MdClose } from "react-icons/md";
import Sidebar from "../Sidebar";
import Layout from "../../layout/Layout";
import OrderDetailsModal from "../AdminBookings/OrderDetailsModal";
import { GET_BOOKINGS } from "../AdminBookings/graphql";
import { GET_BOOKING_CHANGES } from "./graphql";

// Every time a booking was edited (date, slot, services) or verified
// (price, payment, services), and exactly what changed: before -> after,
// field by field, with who did it. From booking_audit - see
// barbera_backend/src/modules/booking-changes.

const PAGE_SIZE = 50;

const PRESETS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "last7", label: "7 days" },
  { key: "last30", label: "30 days" },
  { key: "custom", label: "Custom" },
];

const KINDS = [
  { key: "", label: "All" },
  { key: "EDIT", label: "Edits" },
  { key: "VERIFY", label: "Verifications" },
];

const KIND_CLS = {
  EDIT: "bg-sky-50 text-sky-700",
  VERIFY: "bg-violet-50 text-violet-700",
};

const rupees = (v) => `₹${Math.round(Math.abs(v)).toLocaleString("en-IN")}`;
const when = (iso) => dayjs(iso).format("D MMM, h:mm A");
const show = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));

const Kpi = ({ label, value, sub, valueClass = "text-gray-900" }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
    <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
    <p className={`text-3xl font-semibold mt-1 tabular-nums ${valueClass}`}>{value}</p>
    {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
  </div>
);

const Toggle = ({ on, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-2.5 py-1 rounded-full text-xs border ${on ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"}`}
  >
    {children}
  </button>
);

const PriceDelta = ({ v }) => {
  if (!v) return null;
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold tabular-nums ${v > 0 ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
      {v > 0 ? "+" : "−"}
      {rupees(v)}
    </span>
  );
};

// The before -> after list for one change.
const ChangeList = ({ changes, partial }) => {
  let c = null;
  try {
    c = JSON.parse(changes || "null");
  } catch (e) {
    c = null;
  }
  const s = c?.services || { added: [], removed: [], countChanged: [] };
  const empty = !c || (!c.fields.length && !s.added.length && !s.removed.length && !s.countChanged.length);
  if (empty) return <p className="text-xs text-gray-400">Nothing changed{partial ? " (or this older entry didn't record it)" : ""}.</p>;
  const qty = (n) => (n > 1 ? ` ×${n}` : "");
  return (
    <div className="space-y-1.5">
      {c.fields.length > 0 && (
        <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
          {c.fields.map((f) => (
            <React.Fragment key={f.label}>
              <dt className="text-gray-500">{f.label}</dt>
              <dd className="text-gray-900 break-words">
                <span className="line-through decoration-red-300 text-gray-500">{show(f.old)}</span>
                <span className="mx-1.5 text-gray-400">→</span>
                <span className="font-medium">{show(f.new)}</span>
              </dd>
            </React.Fragment>
          ))}
        </dl>
      )}
      {(s.added.length > 0 || s.removed.length > 0 || s.countChanged.length > 0) && (
        <div className="flex flex-wrap gap-1.5 text-xs">
          {s.added.map((x, i) => (
            <span key={`a${i}`} className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-800">+ {x.name}{qty(x.count)}</span>
          ))}
          {s.removed.map((x, i) => (
            <span key={`r${i}`} className="px-2 py-0.5 rounded bg-red-50 text-red-700 line-through">− {x.name}{qty(x.count)}</span>
          ))}
          {s.countChanged.map((x, i) => (
            <span key={`c${i}`} className="px-2 py-0.5 rounded bg-amber-50 text-amber-800">
              {x.name}: ×{x.oldCount} → ×{x.newCount}
            </span>
          ))}
        </div>
      )}
      {partial && <p className="text-[11px] text-gray-400">Older entry - only the main fields were recorded then.</p>}
    </div>
  );
};

const selectStyles = {
  control: (b) => ({ ...b, minHeight: 38, borderColor: "#e5e7eb", boxShadow: "none" }),
  multiValue: (b) => ({ ...b, backgroundColor: "#FBB033" }),
};

const BookingChanges = () => {
  const [preset, setPreset] = useState("last7");
  const [custom, setCustom] = useState({ start: dayjs().subtract(6, "day").format("YYYY-MM-DD"), end: dayjs().format("YYYY-MM-DD") });
  const [kind, setKind] = useState("");
  const [actorIds, setActorIds] = useState([]);
  const [changedOnly, setChangedOnly] = useState(true);
  const [priceChangedOnly, setPriceChangedOnly] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);

  const [fetchBooking, { loading: opening }] = useMutation(GET_BOOKINGS);
  const [viewingBooking, setViewingBooking] = useState(null);
  const [openError, setOpenError] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchText.trim()), 400);
    return () => clearTimeout(t);
  }, [searchText]);
  useEffect(() => setPage(0), [preset, custom, kind, actorIds, changedOnly, priceChangedOnly, search]);

  const range = useMemo(() => {
    const today = dayjs().format("YYYY-MM-DD");
    if (preset === "today") return { startDate: today, endDate: today };
    if (preset === "yesterday") {
      const y = dayjs().subtract(1, "day").format("YYYY-MM-DD");
      return { startDate: y, endDate: y };
    }
    if (preset === "last30") return { startDate: dayjs().subtract(29, "day").format("YYYY-MM-DD"), endDate: today };
    if (preset === "custom") return { startDate: custom.start, endDate: custom.end };
    return { startDate: dayjs().subtract(6, "day").format("YYYY-MM-DD"), endDate: today };
  }, [preset, custom]);

  // People list comes from the unfiltered-by-person query, so picking one
  // doesn't empty the dropdown.
  const baseFilter = { ...range, kinds: kind ? [kind] : [], changedOnly, priceChangedOnly, search };
  const { data, loading, error } = useQuery(GET_BOOKING_CHANGES, {
    variables: { filter: { ...baseFilter, actorIds }, limit: PAGE_SIZE, offset: page * PAGE_SIZE },
    fetchPolicy: "cache-and-network",
  });
  const { data: peopleData } = useQuery(GET_BOOKING_CHANGES, {
    variables: { filter: baseFilter, limit: 1, offset: 0 },
    fetchPolicy: "cache-and-network",
  });
  const res = data?.getBookingChanges;
  const d = res?.data;
  const errorMessage = error?.message || (res?.status === "ERROR" && res.message);

  const allActors = peopleData?.getBookingChanges?.data?.actors || d?.actors || [];
  const actorOptions = useMemo(
    () => allActors.filter((a) => a.id).map((a) => ({ value: String(a.id), label: `${a.name}${a.type === "EMPLOYEE" ? " (professional)" : ""}` })),
    [allActors]
  );

  const openBooking = async (r) => {
    setOpenError("");
    try {
      const out = await fetchBooking({ variables: { data: { id: r.orderId, bookingId: r.bookingId } } });
      const order = (out.data?.getBookings?.orders || []).find((o) => String(o.id) === String(r.orderId));
      if (!order) throw new Error(out.data?.getBookings?.message || "Booking not found");
      setViewingBooking(order);
    } catch (e) {
      setOpenError(`Could not open the booking: ${e.message}`);
    }
  };

  const pickActor = (id) => {
    if (!id) return;
    setActorIds([String(id)]);
    setTimeout(() => document.getElementById("bc-feed")?.scrollIntoView({ behavior: "smooth" }), 50);
  };
  const pages = d ? Math.ceil(d.total / PAGE_SIZE) : 0;
  const sum = d?.summary;

  return (
    <div className="min-h-screen flex">
      <Sidebar />
      <Layout>
        <div className="max-w-7xl space-y-5 pb-16">
          <div>
            <h1 className="text-2xl font-semibold text-white">Booking changes</h1>
            <p className="text-sm text-gray-400 mt-0.5">
              Every edit and verification of a booking, and exactly what it changed - date, slot, services, price, payment, address - with who did it.
            </p>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3 flex flex-wrap items-center gap-3">
            <div className="flex rounded-lg bg-gray-100 p-0.5">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setPreset(p.key)}
                  className={`px-3 py-1.5 rounded-md text-sm ${preset === p.key ? "bg-white shadow-sm font-semibold text-gray-900" : "text-gray-600 hover:text-gray-900"}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {preset === "custom" && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <input type="date" value={custom.start} max={custom.end} onChange={(e) => e.target.value && setCustom((c) => ({ ...c, start: e.target.value }))} className="border border-gray-200 rounded-lg px-2 py-1.5" />
                to
                <input type="date" value={custom.end} min={custom.start} onChange={(e) => e.target.value && setCustom((c) => ({ ...c, end: e.target.value }))} className="border border-gray-200 rounded-lg px-2 py-1.5" />
              </div>
            )}
            <div className="flex rounded-lg bg-gray-100 p-0.5">
              {KINDS.map((k) => (
                <button
                  key={k.key}
                  type="button"
                  onClick={() => setKind(k.key)}
                  className={`px-3 py-1.5 rounded-md text-sm ${kind === k.key ? "bg-white shadow-sm font-semibold text-gray-900" : "text-gray-600 hover:text-gray-900"}`}
                >
                  {k.label}
                </button>
              ))}
            </div>
            <div className="min-w-[220px] flex-1 max-w-sm">
              <Select
                isMulti
                placeholder="Everyone"
                options={actorOptions}
                value={actorOptions.filter((o) => actorIds.includes(o.value))}
                onChange={(sel) => setActorIds(sel ? sel.map((x) => x.value) : [])}
                styles={selectStyles}
              />
            </div>
            <div className="relative min-w-[200px]">
              <MdOutlineSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Booking id, customer, professional"
                className="w-full border border-gray-200 rounded-lg pl-8 pr-7 py-2 text-sm"
              />
              {searchText && (
                <button type="button" onClick={() => setSearchText("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700">
                  <MdClose />
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <Toggle on={changedOnly} onClick={() => setChangedOnly((v) => !v)}>Only real changes</Toggle>
              <Toggle on={priceChangedOnly} onClick={() => setPriceChangedOnly((v) => !v)}>Price changed</Toggle>
            </div>
          </div>

          {errorMessage && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">Could not load booking changes: {errorMessage}</div>}
          {res?.status === "SUCCESS" && res.message && <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-3 text-sm">{res.message}</div>}
          {openError && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">{openError}</div>}
          {loading && !d && <div className="py-16 text-center text-gray-400">Loading…</div>}

          {d && sum && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <Kpi label="Changes" value={sum.changes.toLocaleString("en-IN")} sub={`${sum.edits} edits · ${sum.verifications} verifications`} />
                <Kpi label="Bookings touched" value={sum.bookings.toLocaleString("en-IN")} />
                <Kpi label="Price changed" value={sum.priceChanged} sub="changes that moved the amount" />
                <Kpi label="Price raised" value={rupees(sum.priceUp)} valueClass={sum.priceUp ? "text-emerald-600" : "text-gray-900"} sub="total added to bills" />
                <Kpi label="Price cut" value={rupees(sum.priceDown)} valueClass={sum.priceDown ? "text-red-600" : "text-gray-900"} sub="total taken off bills" />
              </div>

              <div className="grid lg:grid-cols-3 gap-5">
                <section className="lg:col-span-2 bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                  <h2 className="font-semibold text-gray-900">By person</h2>
                  <p className="text-xs text-gray-500 mt-0.5">Click a name to see only their changes.</p>
                  <div className="overflow-x-auto -mx-4 mt-3">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                          {["Person", "Changes", "Edits", "Verifications", "Price raised", "Price cut"].map((h) => (
                            <th key={h} className="px-3 py-2 font-medium whitespace-nowrap first:pl-4">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {d.actors.map((a) => (
                          <tr key={a.id || "unknown"} className="border-b border-gray-50 hover:bg-gray-50 tabular-nums">
                            <td className="px-3 pl-4 py-2 whitespace-nowrap">
                              <button type="button" disabled={!a.id} onClick={() => pickActor(a.id)} className="font-medium text-gray-900 hover:underline disabled:no-underline disabled:text-gray-400">
                                {a.name}
                              </button>
                              {a.type === "EMPLOYEE" && <span className="ml-2 text-[11px] text-gray-400">professional</span>}
                            </td>
                            <td className="px-3 py-2">{a.changes}</td>
                            <td className="px-3 py-2">{a.edits}</td>
                            <td className="px-3 py-2">{a.verifications}</td>
                            <td className="px-3 py-2 text-emerald-700">{a.priceUp ? rupees(a.priceUp) : "—"}</td>
                            <td className="px-3 py-2 text-red-700">{a.priceDown ? rupees(a.priceDown) : "—"}</td>
                          </tr>
                        ))}
                        {!d.actors.length && (
                          <tr>
                            <td colSpan={6} className="px-4 py-6 text-center text-gray-400">No changes in this period.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </section>

                <section className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                  <h2 className="font-semibold text-gray-900">What gets changed</h2>
                  <p className="text-xs text-gray-500 mt-0.5">How many changes touched each field.</p>
                  <ul className="mt-3 space-y-1.5">
                    {d.fields.map((f) => {
                      const max = d.fields[0]?.count || 1;
                      return (
                        <li key={f.label} className="text-sm">
                          <div className="flex justify-between text-gray-700">
                            <span>{f.label}</span>
                            <span className="tabular-nums text-gray-500">{f.count}</span>
                          </div>
                          <div className="h-1.5 rounded bg-gray-100 mt-0.5">
                            <div className="h-1.5 rounded bg-[#FBB033]" style={{ width: `${(f.count / max) * 100}%` }} />
                          </div>
                        </li>
                      );
                    })}
                    {!d.fields.length && <li className="text-sm text-gray-400">Nothing yet.</li>}
                  </ul>
                </section>
              </div>

              <section id="bc-feed" className="bg-white rounded-xl border border-gray-100 shadow-sm">
                <header className="flex flex-wrap items-center justify-between gap-2 px-4 pt-4">
                  <div>
                    <h2 className="font-semibold text-gray-900">All changes</h2>
                    <p className="text-xs text-gray-500 mt-0.5">Newest first. Struck-through is before, bold is after.</p>
                  </div>
                  {actorIds.length > 0 && (
                    <button type="button" onClick={() => setActorIds([])} className="text-xs text-gray-500 hover:text-gray-900 underline">
                      Show everyone
                    </button>
                  )}
                </header>
                <ul className="divide-y divide-gray-100 mt-3">
                  {d.entries.map((e) => (
                    <li key={e.id} className="px-4 py-3 grid md:grid-cols-[260px_1fr] gap-3">
                      <div className="text-sm space-y-0.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${KIND_CLS[e.kind] || "bg-gray-100 text-gray-600"}`}>
                            {e.kind === "VERIFY" ? "Verified" : "Edited"}
                          </span>
                          <button type="button" disabled={opening} onClick={() => openBooking(e)} className="font-semibold text-gray-900 hover:underline inline-flex items-center gap-1" title="Open booking">
                            {e.bookingId || e.orderId.slice(0, 8)}
                            <MdOutlineOpenInNew className="text-gray-400" />
                          </button>
                          <PriceDelta v={e.priceDelta} />
                        </div>
                        <p className="text-gray-500 text-xs">
                          {e.customerName || "Customer"}
                          {e.professionalName ? ` · ${e.professionalName}` : ""}
                          {e.bookingDate ? ` · for ${dayjs(e.bookingDate).format("D MMM")}` : ""}
                        </p>
                        <p className="text-xs text-gray-700">
                          by <span className="font-medium">{e.actorName || "Unknown"}</span>
                          {e.actorType === "EMPLOYEE" ? " (professional)" : ""} · {when(e.createdAt)}
                        </p>
                        {e.reason && <p className="text-xs text-gray-500 italic break-words">“{e.reason}”</p>}
                      </div>
                      <ChangeList changes={e.changes} partial={e.partial} />
                    </li>
                  ))}
                  {!d.entries.length && <li className="px-4 py-10 text-center text-gray-400 text-sm">No changes match these filters.</li>}
                </ul>
                {pages > 1 && (
                  <footer className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm text-gray-600">
                    <span>
                      {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, d.total)} of {d.total}
                    </span>
                    <div className="flex gap-2">
                      <button type="button" disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="px-3 py-1 rounded border border-gray-200 disabled:opacity-40">
                        Previous
                      </button>
                      <button type="button" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)} className="px-3 py-1 rounded border border-gray-200 disabled:opacity-40">
                        Next
                      </button>
                    </div>
                  </footer>
                )}
              </section>
            </>
          )}
        </div>
      </Layout>
      {viewingBooking && <OrderDetailsModal booking={viewingBooking} onClose={() => setViewingBooking(null)} />}
    </div>
  );
};

export default BookingChanges;
